/**
 * WorkspaceGuard.js — RC 1.30.B0 route-level access check.
 *
 * Wraps each workspace section. Redirects to the user's effective
 * default workspace if they lack access to the one they're hitting
 * directly (e.g. a candidate-only user typing `/employer` in the
 * URL bar). Redirects to `/no-access` when the user has zero
 * workspaces, and to `/choose-workspace` when they have multiple
 * workspaces with no valid stored default (and they're on the
 * bare root `/`).
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useWorkspace, landingPathFor } from '@/contexts/WorkspaceContext';

export default function WorkspaceGuard({ workspace, children }) {
  const { access, loading, allowedWorkspaces, effectiveDefault } = useWorkspace();
  const location = useLocation();

  // First-paint: wait for /api/auth/me/access to resolve so we
  // don't bounce the user between routes before we know the truth.
  if (!access && loading) return null;
  if (!access) return null;

  // No workspaces at all → send to the NoAccess page.
  if (!allowedWorkspaces.length) {
    if (location.pathname === '/no-access') return children;
    return <Navigate to="/no-access" replace />;
  }

  // User is hitting a workspace they're not allowed in.
  if (workspace && !allowedWorkspaces.includes(workspace)) {
    return <Navigate to={landingPathFor(effectiveDefault)} replace />;
  }

  return children;
}
